import React, { createContext, useContext, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>(() => {
    const savedTheme = localStorage.getItem('app-theme') as Theme | null;
    if (savedTheme === 'light' || savedTheme === 'dark') {
      return savedTheme;
    }
    return 'dark'; // Portal default theme, completely independent of browser theme
  });

  useEffect(() => {
    const root = document.documentElement;

    // 1. Temporarily disable transitions during theme switch so all DOM elements update atomically
    const style = document.createElement('style');
    style.appendChild(
      document.createTextNode(
        `html, body, *, *::before, *::after {
          transition: none !important;
          -webkit-transition: none !important;
        }`
      )
    );
    document.head.appendChild(style);

    // 2. Atomic class and scheme switch
    if (theme === 'dark') {
      root.classList.remove('light');
      root.classList.add('dark');
      root.setAttribute('data-theme', 'dark');
      root.style.colorScheme = 'dark';
    } else {
      root.classList.remove('dark');
      root.classList.add('light');
      root.setAttribute('data-theme', 'light');
      root.style.colorScheme = 'light';
    }
    localStorage.setItem('app-theme', theme);

    // 3. Force synchronous style/paint recalculation to commit the new theme cleanly in 1 frame
    void window.getComputedStyle(document.body).opacity;

    // 4. Re-enable transitions on the next animation frame for smooth UI hover and animations
    const frameId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (style.parentNode) {
          style.parentNode.removeChild(style);
        }
      });
    });

    return () => {
      cancelAnimationFrame(frameId);
      if (style.parentNode) {
        style.parentNode.removeChild(style);
      }
    };
  }, [theme]);

  const toggleTheme = () => {
    setThemeState(prev => (prev === 'dark' ? 'light' : 'dark'));
  };

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
